import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { createStandaloneCharge } from '../core/subscriptionService';
import { getClientAll } from '../../../services/clientService';
import { getUnitBranchAllSimple } from '../../../services/unitBranchService';
import { getServicesAll } from '../../../services/servicesService';
import { getProposalsAll } from '../../../services/proposalsService';
import { useToast } from '../../components/Toats';
import { getReactSelectStyles } from '../core/reactSelectTheme';
import { useThemeMode } from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

interface Props {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

export const StandaloneChargeModal: React.FC<Props> = ({ show, onHide, onSuccess }) => {
  const { toggleToast } = useToast();
  const { mode } = useThemeMode();
  const selectStyles = useMemo(() => getReactSelectStyles(), [mode]);

  const [branches, setBranches] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [proposals, setProposals] = useState<any[]>([]);

  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sourceType, setSourceType] = useState<'NENHUMA' | 'OS' | 'PROPOSTA'>('NENHUMA');
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [selectedProposalId, setSelectedProposalId] = useState('');
  const [grossValue, setGrossValue] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'BOLETO' | 'PIX'>('BOLETO');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (show) {
      getUnitBranchAllSimple().then(data => {
        setBranches(data?.data || data || []);
      });
    }
  }, [show]);

  useEffect(() => {
    if (selectedBranchId) {
      getClientAll(selectedBranchId).then(data => {
        setClients(data?.data || data || []);
      }).catch(() => setClients([]));
    } else {
      setClients([]);
    }
    setSelectedClientId('');
  }, [selectedBranchId]);

  useEffect(() => {
    if (selectedClientId && selectedBranchId) {
      getServicesAll(undefined, undefined, selectedBranchId, undefined, undefined, undefined, selectedClientId).then(data => {
        const validServices = (data?.data || data || []).filter((os: any) => 
          os.status !== 'CANCELADO'
        );
        setServices(validServices);
      }).catch(() => setServices([]));

      getProposalsAll(undefined, undefined, undefined, undefined, undefined, selectedClientId).then(data => {
        const validProposals = (data?.data || data || []).filter((prop: any) => 
          prop.status !== 'NEGOCIO RECUSADO'
        );
        setProposals(validProposals);
      }).catch(() => setProposals([]));
    } else {
      setServices([]);
      setProposals([]);
    }
    setSelectedServiceId('');
    setSelectedProposalId('');
    setGrossValue('');
    setDescription('');
  }, [selectedClientId, selectedBranchId]);

  const resetForm = () => {
    setSelectedBranchId('');
    setSelectedClientId('');
    setSourceType('NENHUMA');
    setSelectedServiceId('');
    setSelectedProposalId('');
    setGrossValue('');
    setDueDate('');
    setPaymentMethod('BOLETO');
    setDescription('');
    setErrors({});
  };

  const formatCurrency = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    if (!cleanValue) return '';
    const numberValue = Number(cleanValue) / 100;
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numberValue);
  };

  const getNumericValue = (val: string) => {
    if (!val) return 0;
    return Number(val.replace(/\D/g, '')) / 100;
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleSave = () => {
    const numericGrossValue = getNumericValue(grossValue);
    const newErrors: Record<string, string> = {};

    if (!selectedBranchId) newErrors.branch = 'A filial é obrigatória.';
    if (!selectedClientId) newErrors.client = 'O cliente é obrigatório.';
    if (!numericGrossValue) newErrors.value = 'O valor deve ser maior que zero.';
    if (!dueDate) newErrors.date = 'A data de vencimento é obrigatória.';

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setSaving(true);
    createStandaloneCharge({
      client_id: selectedClientId,
      unitBranch_id: selectedBranchId,
      gross_value: numericGrossValue,
      due_date: dueDate,
      payment_method: paymentMethod,
      description,
      ...(sourceType === 'OS' && selectedServiceId ? { service_id: selectedServiceId } : {}),
      ...(sourceType === 'PROPOSTA' && selectedProposalId ? { proposal_id: selectedProposalId } : {}),
    })
      .then(() => {
        toggleToast('Cobrança avulsa criada com sucesso!', 'success');
        resetForm();
        onSuccess();
      })
      .catch((err: any) => {
        toggleToast(err?.response?.data?.error || err?.response?.data?.message || 'Erro ao criar cobrança avulsa.', 'danger');
      })
      .finally(() => setSaving(false));
  };

  if (!show) return null;

  return (
    <>
      <div className='modal fade show d-block' tabIndex={-1} role='dialog' aria-modal='true'>
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2 className='fw-bolder'>Nova Cobrança Avulsa</h2>
              <div className='btn btn-icon btn-sm btn-active-icon-primary' onClick={handleClose}>
                <i className='bi bi-x fs-1'></i>
              </div>
            </div>

            <div className='modal-body scroll-y mx-5 mx-xl-15 my-7'>
              <div className='d-flex flex-column mb-5'>
                <label className='fs-6 fw-bold mb-2'>
                  Filial <span className='text-danger'>*</span>
                </label>
                <Select
                  options={branches.map(b => ({ value: b.id, label: b.companyName || b.name || 'Filial Sem Nome' }))}
                  value={selectedBranchId ? { value: selectedBranchId, label: branches.find(b => b.id === selectedBranchId)?.companyName || branches.find(b => b.id === selectedBranchId)?.name || 'Filial' } : null}
                  onChange={(val: any) => {
                    setSelectedBranchId(val?.value || '');
                    setErrors({ ...errors, branch: '' });
                  }}
                  placeholder='Selecione a filial'
                  className='react-select-container'
                  classNamePrefix='react-select'
                  styles={selectStyles}
                  isClearable
                />
                {errors.branch && <div className='text-danger mt-2 fs-7'>{errors.branch}</div>}
              </div>

              <div className='d-flex flex-column mb-5'>
                <label className='fs-6 fw-bold mb-2'>
                  Cliente <span className='text-danger'>*</span>
                </label>
                <Select
                  options={clients.map(c => ({ value: c.id, label: c.nameResponsible || c.name || 'Cliente Sem Nome' }))}
                  value={selectedClientId ? { value: selectedClientId, label: clients.find(c => c.id === selectedClientId)?.nameResponsible || clients.find(c => c.id === selectedClientId)?.name || 'Cliente' } : null}
                  onChange={(val: any) => {
                    setSelectedClientId(val?.value || '');
                    setErrors({ ...errors, client: '' });
                  }}
                  placeholder={selectedBranchId ? 'Selecione um cliente' : 'Selecione uma filial primeiro'}
                  className='react-select-container'
                  classNamePrefix='react-select'
                  styles={selectStyles}
                  isDisabled={!selectedBranchId}
                  isClearable
                />
                {errors.client && <div className='text-danger mt-2 fs-7'>{errors.client}</div>}
              </div>

              {selectedClientId && (
                <>
                  <div className='d-flex flex-column mb-5'>
                    <label className='fs-6 fw-bold mb-3'>Origem (Opcional)</label>
                    <div className='d-flex align-items-center gap-5'>
                      <label className='form-check form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='sourceType'
                          value='NENHUMA'
                          checked={sourceType === 'NENHUMA'}
                          onChange={() => {
                            setSourceType('NENHUMA');
                            setSelectedServiceId('');
                            setSelectedProposalId('');
                            setGrossValue('');
                            setDescription('');
                          }}
                        />
                        <span className='form-check-label'>Nenhuma</span>
                      </label>
                      <label className='form-check form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='sourceType'
                          value='OS'
                          checked={sourceType === 'OS'}
                          onChange={() => {
                            setSourceType('OS');
                            setSelectedProposalId('');
                            setGrossValue('');
                            setDescription('');
                          }}
                        />
                        <span className='form-check-label'>Ordem de Serviço</span>
                      </label>
                      <label className='form-check form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='sourceType'
                          value='PROPOSTA'
                          checked={sourceType === 'PROPOSTA'}
                          onChange={() => {
                            setSourceType('PROPOSTA');
                            setSelectedServiceId('');
                            setGrossValue('');
                            setDescription('');
                          }}
                        />
                        <span className='form-check-label'>Proposta</span>
                      </label>
                    </div>
                  </div>

                  {sourceType === 'OS' && (
                    <div className='d-flex flex-column mb-5'>
                      <label className='fs-6 fw-bold mb-2'>Selecione a OS</label>
                      <Select
                        options={services.map(os => ({ value: os.id, label: `OS #${os.number_order_service} - ${os.title || 'Sem título'}`, data: os }))}
                        value={selectedServiceId ? { value: selectedServiceId, label: `OS #${services.find(s => s.id === selectedServiceId)?.number_order_service} - ${services.find(s => s.id === selectedServiceId)?.title || 'Sem título'}` } : null}
                        onChange={(val: any) => {
                          setSelectedServiceId(val?.value || '');
                          if (val?.data) {
                            setGrossValue(formatCurrency((val.data.value_final * 100).toString()));
                            setDescription(`OS #${val.data.number_order_service} - ${val.data.title || 'Sem título'}`);
                          }
                        }}
                        placeholder='Nenhuma OS selecionada'
                        className='react-select-container'
                        classNamePrefix='react-select'
                        styles={selectStyles}
                        isClearable
                        noOptionsMessage={() => 'Nenhuma OS encontrada para este cliente'}
                      />
                    </div>
                  )}

                  {sourceType === 'PROPOSTA' && (
                    <div className='d-flex flex-column mb-5'>
                      <label className='fs-6 fw-bold mb-2'>Selecione a Proposta</label>
                      <Select
                        options={proposals.map(p => ({ value: p.id, label: p.title || 'Proposta sem título', data: p }))}
                        value={selectedProposalId ? { value: selectedProposalId, label: proposals.find(p => p.id === selectedProposalId)?.title || 'Proposta sem título' } : null}
                        onChange={(val: any) => {
                          setSelectedProposalId(val?.value || '');
                          if (val?.data) {
                            setGrossValue(formatCurrency((val.data.value_final * 100).toString()));
                            setDescription(val.data.title || 'Proposta sem título');
                          }
                        }}
                        placeholder='Nenhuma proposta selecionada'
                        className='react-select-container'
                        classNamePrefix='react-select'
                        styles={selectStyles}
                        isClearable
                        noOptionsMessage={() => 'Nenhuma proposta encontrada para este cliente'}
                      />
                    </div>
                  )}
                </>
              )}

              <div className='row g-5 mb-5'>
                <div className='col-md-6'>
                  <label className='fs-6 fw-bold mb-2'>
                    Valor (R$) <span className='text-danger'>*</span>
                  </label>
                  <div className='input-group'>
                    <span className='input-group-text'>R$</span>
                    <input
                      type='text'
                      className='form-control form-control-solid'
                      placeholder='0,00'
                      value={grossValue}
                      onChange={e => {
                        setGrossValue(formatCurrency(e.target.value));
                        setErrors({ ...errors, value: '' });
                      }}
                    />
                  </div>
                  {errors.value && <div className='text-danger mt-2 fs-7'>{errors.value}</div>}
                </div>
                <div className='col-md-6'>
                  <label className='fs-6 fw-bold mb-2'>
                    Data de Vencimento <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='date'
                    className='form-control form-control-solid'
                    value={dueDate}
                    onChange={e => {
                      setDueDate(e.target.value);
                      setErrors({ ...errors, date: '' });
                    }}
                  />
                  {errors.date && <div className='text-danger mt-2 fs-7'>{errors.date}</div>}
                </div>
              </div>

              <div className='d-flex flex-column mb-5'>
                <label className='fs-6 fw-bold mb-2'>Método de Pagamento</label>
                <select
                  className='form-select form-select-solid'
                  value={paymentMethod}
                  onChange={e => setPaymentMethod(e.target.value as 'BOLETO' | 'PIX')}
                >
                  <option value='BOLETO'>Boleto</option>
                  <option value='PIX'>Pix</option>
                </select>
              </div>

              <div className='d-flex flex-column mb-8'>
                <label className='fs-6 fw-bold mb-2'>Descrição</label>
                <textarea
                  className='form-control form-control-solid'
                  rows={3}
                  placeholder='Descreva o motivo desta cobrança...'
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                ></textarea>
              </div>

              <div className='text-center pt-5'>
                <button type='button' className='btn btn-light me-3' onClick={handleClose}>
                  Cancelar
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Criando... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  ) : (
                    <span className='indicator-label'>Criar Cobrança</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  );
};
